import { createAPIMethod } from 'utils/generic';
import { normalizeUserData, type ResponseUserData, type UserData, type Verification } from './model';
import { normalizeToCamelCase } from 'utils/normalize';
import { PaymentStatus, PaymentType } from 'utils/constants';
import { cacheKeyPostfixByUser } from './utils';

export type UsersSelfParams = {
	use_cache?: boolean;
};

export async function getUserSelf(params?: UsersSelfParams, headers?: Headers) {
	return createAPIMethod<UsersSelfParams, UserData>({
		method: 'GET',
		path: '/users/self',
		normalize: ({ result }) => normalizeUserData(result as ResponseUserData),
		headers,
		cacheKeyPostfix: cacheKeyPostfixByUser(headers),
		useCache: headers?.get('cache-control') !== 'no-cache',
		cacheTtl: 1000 * 60 * 60,
	})(params);
}

export async function getUserByShopUrl(params: { shop_url: string }, headers?: Headers) {
	return createAPIMethod<object, UserData>({
		method: 'GET',
		path: `/users/public`,
		normalize: ({ result }) => normalizeUserData(result as ResponseUserData),
		headers,
	})(params);
}

interface UpdateUserSelfParams {
	shop_description?: string;
	shop_name?: string;
	shop_url?: string;
	settings?: {
		listing_row_view?: boolean;
	};
}

export function patchUserSelf(params: UpdateUserSelfParams, headers?: Headers) {
	return createAPIMethod<UpdateUserSelfParams, { status_code: number; status_message: string }>({
		method: 'PATCH',
		path: '/users/self',
		skipResponseParsing: true,
		headers,
	})(params);
}

export function deletePortrait(id: number, headers?: Headers) {
	return createAPIMethod({
		method: 'DELETE',
		path: `/users/${id}/portrait`,
		skipResponseParsing: true,
		headers,
	})();
}

interface UpdatePortraitVisibilityParams {
	portrait_visible: boolean;
}

export function patchPortraitVisibility(params: UpdatePortraitVisibilityParams, headers?: Headers) {
	return createAPIMethod<UpdatePortraitVisibilityParams, { status_code: number; status_message: string }>({
		method: 'PATCH',
		path: `/users/self`,
		skipResponseParsing: true,
		headers,
	})(params);
}

interface PostPortraitImageParams {
	blob: Blob;
	userId: number;
}

export function postPortraitImage({ blob, userId }: PostPortraitImageParams, headers?: Headers) {
	const formData = new FormData();
	formData.append('file', blob);

	return createAPIMethod<FormData>({
		path: `/users/${userId}/portrait`,
		method: 'POST',
		skipResponseParsing: true,
		headers,
	})(formData);
}

export type AvailablePayment =
	| {
			available: boolean;
			count: number;
			credit?: undefined;
			hasWallet?: undefined;
			operatorPrice: number;
			paymentType: (typeof PaymentType)['SMS'];
			price: number;
	  }
	| {
			available: boolean;
			count: number;
			credit?: undefined;
			hasWallet?: undefined;
			operatorPrice?: undefined;
			paymentType: (typeof PaymentType)['CARD'];
			price: number;
	  }
	| {
			available: boolean;
			count: number;
			credit: number;
			hasWallet: boolean;
			operatorPrice?: undefined;
			paymentType: (typeof PaymentType)['WALLET'];
			price: number;
	  }
	| {
			available: boolean;
			count: number;
			credit: number;
			hasWallet?: undefined;
			operatorPrice?: undefined;
			paymentType: (typeof PaymentType)['VOUCHER'];
			price: number;
	  };

export function getAvailablePayments(headers?: HeadersInit): Promise<AvailablePayment[]> {
	return createAPIMethod<object, AvailablePayment[]>({
		method: 'GET',
		path: '/users/available_payments',
		normalize: ({ results, vouchers }: { results?: AvailablePayment[]; vouchers?: number }) => {
			if (vouchers === undefined) {
				return Object.values(normalizeToCamelCase(results || []));
			}

			if (!results) {
				return [];
			}

			return [
				{
					available: Boolean(vouchers),
					credit: vouchers,
					count: 1,
					paymentType: PaymentType.VOUCHER,
					price: 0,
				},
				...Object.values(normalizeToCamelCase(results || [])),
			];
		},
		headers,
	})();
}

export type PayToppingParams =
	| {
			paymentType: (typeof PaymentType)['CARD'] | (typeof PaymentType)['WALLET'];
			vouchers?: number;
			failUrl?: string;
			returnUrl?: string;
	  }
	| {
			paymentType: (typeof PaymentType)['VOUCHER'];
			vouchers: number;
			failUrl?: string;
			returnUrl?: string;
	  };

export function payTopping(
	{ paymentType, vouchers = undefined, failUrl = undefined, returnUrl = undefined }: PayToppingParams,
	headers?: HeadersInit,
) {
	const newHeaders = new Headers(headers);
	newHeaders.set('Accept', 'application/json');
	newHeaders.set('Content-Type', 'application/json');

	return createAPIMethod({
		path: `/users/payments`,
		method: 'POST',
		skipResponseParsing: true,
		headers: newHeaders,
	})({
		payment_type: paymentType,
		vouchers,
		fail_url: failUrl,
		return_url: returnUrl,
	});
}

export type checkPaymentStatusResponse = {
	statusCode: number;
	paymentStatus: (typeof PaymentStatus)[keyof typeof PaymentStatus];
	errors: { errorCode: number | string; errorData: any }[];
};

export function checkPaymentStatus(paymentId: number | string, params: any = {}, headers?: HeadersInit) {
	return createAPIMethod<object, checkPaymentStatusResponse>({
		path: `/users/payments/${paymentId}`,
		method: 'PUT',
		suppressErrorThrowing: true,
		headers,
	})(params);
}

export function establishUser(headers?: HeadersInit) {
	const newHeaders = new Headers(headers);
	newHeaders.set('Accept', 'application/json');
	newHeaders.set('Content-Type', 'application/json');

	return createAPIMethod({
		path: '/users',
		method: 'POST',
		headers: newHeaders,
		skipResponseParsing: true,
	})({
		service_name: 'sbazar',
	});
}

type SelfVerificationResponse = {
	statusCode: number;
	statusMessage: string;
	verification: Verification;
};

export function getSelfVerificationStatus(headers?: HeadersInit) {
	return createAPIMethod<undefined, SelfVerificationResponse>({
		path: '/users/self/verification',
		method: 'PATCH',
		headers,
	})();
}
